/**
 * iter 63 — UNIFIED DRAWER LIFECYCLE PRIMITIVES.
 *
 * Three shared bits every entity-manager drawer in the app uses so they
 * all look + behave identically:
 *
 *   useDrawerForm({open, isEdit, initialData, emptyForm, hydrate})
 *     - Centralises `form`, `busy`, `err` state.
 *     - Hydrates `form` on open from either `emptyForm` (create mode)
 *       or `hydrate(initialData)` (edit mode), and resets on close.
 *     - Computes `isDirty` against the original snapshot.
 *     - Exposes `requestClose(actualClose)` that prompts on dirty close
 *       so users never lose work to a misclicked backdrop / Esc.
 *
 *   <DrawerFooter onCancel save />
 *     A sticky right-aligned footer with a CANCEL ghost button and a
 *     CREATE / UPDATE primary button. Hands off to <SaveButton> for the
 *     spinner / disabled wiring. Plug this into <Drawer footer={...}>.
 *
 *   <SaveButton mode busy /> + <ErrorBanner err /> + <Spinner/>
 *     Pre-existing helpers — re-exported here as the single canonical
 *     surface so drawer files only need one import.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Warning, CircleNotch } from "@phosphor-icons/react";

// ===========================================================================
// Existing helpers (kept verbatim — drawers already import these names)
// ===========================================================================

export const Spinner = ({ size = 12 }) => (
  <CircleNotch size={size} className="animate-spin" />
);

export function SaveButton({ mode, label, busy, disabled, onClick, testid }) {
  const isEdit = mode === "edit";
  const text = label || (isEdit ? "Update" : "Create");
  return (
    <button
      type="button"
      className="btn btn-primary"
      onClick={onClick}
      disabled={busy || disabled}
      data-testid={testid}
    >
      {busy ? (
        <>
          <Spinner /> {isEdit ? "Updating…" : "Creating…"}
        </>
      ) : text}
    </button>
  );
}

export function ErrorBanner({ err, testid = "entity-form-error" }) {
  if (!err) return null;
  return (
    <div
      className="surface p-2.5 mb-3 text-xs text-destructive border-destructive bg-destructive/10 flex items-start gap-2"
      data-testid={testid}
    >
      <Warning size={14} className="mt-0.5 flex-shrink-0" />
      <div className="flex-1">{err}</div>
    </div>
  );
}

// ===========================================================================
// iter 63 — sticky drawer footer + lifecycle hook
// ===========================================================================

/**
 * Sticky Cancel / Save footer for entity-manager drawers. Pass directly to
 * `<Drawer footer={<DrawerFooter .../>}>`. Cancel always triggers `onCancel`
 * (which should be wired to `useDrawerForm.requestClose(...)`); the primary
 * Save button delegates to `<SaveButton>` so spinner + label switching is
 * uniform. An optional `left` slot lets you mount destructive actions
 * (e.g., "Delete user") on the far left.
 */
export function DrawerFooter({
  onCancel, onSave, mode, busy, saveDisabled = false,
  saveLabel, cancelLabel = "Cancel", testid, left,
}) {
  return (
    <div
      className="flex items-center justify-between gap-2 w-full"
      data-testid={testid || "drawer-footer"}
    >
      <div className="flex items-center gap-2">{left}</div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={onCancel}
          disabled={busy}
          data-testid={testid ? `${testid}-cancel` : "drawer-footer-cancel"}
        >
          {cancelLabel}
        </button>
        <SaveButton
          mode={mode}
          label={saveLabel}
          busy={busy}
          disabled={saveDisabled}
          onClick={onSave}
          testid={testid ? `${testid}-save` : "drawer-footer-save"}
        />
      </div>
    </div>
  );
}

const _shallowEqual = (a, b) => {
  if (a === b) return true;
  if (!a || !b) return false;
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  for (const k of ka) {
    const av = a[k];
    const bv = b[k];
    if (Array.isArray(av) && Array.isArray(bv)) {
      if (av.length !== bv.length) return false;
      for (let i = 0; i < av.length; i++) if (av[i] !== bv[i]) return false;
    } else if (av !== bv) {
      return false;
    }
  }
  return true;
};

/**
 * Standardised lifecycle for every entity-manager drawer.
 *
 * @param {object}   args
 * @param {boolean}  args.open          — controls hydrate / reset.
 * @param {boolean}  args.isEdit        — picks the empty form vs. hydration source.
 * @param {object?}  args.initialData   — record to edit; ignored unless isEdit.
 * @param {object}   args.emptyForm     — shape used in create mode + as dirty baseline.
 * @param {function} args.hydrate       — (initialData) => formShape; called in edit mode.
 *
 * @returns {{
 *    form, setForm, setField,
 *    busy, setBusy,
 *    err,  setErr,
 *    isDirty,
 *    requestClose,      // pass to <Drawer onClose={requestClose} />
 *  }}
 */
export function useDrawerForm({ open, isEdit, initialData, emptyForm, hydrate }) {
  const [form, setForm] = useState(emptyForm);
  const [original, setOriginal] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Re-hydrate every time the drawer opens. `hydrate` is intentionally NOT
  // listed as a dependency — callers usually re-create the closure per
  // render, which would otherwise thrash the form mid-edit.
  useEffect(() => {
    if (!open) return;
    const next = isEdit && initialData
      ? (hydrate ? hydrate(initialData) : { ...initialData })
      : emptyForm;
    setForm(next);
    setOriginal(next);
    setErr("");
    setBusy(false);
  }, [open, isEdit, initialData]);

  const setField = useCallback((key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
  }, []);

  const isDirty = useMemo(() => !_shallowEqual(form, original), [form, original]);

  const requestClose = useCallback((actualClose) => {
    if (busy) return;                  // never close mid-save
    if (!isDirty) { actualClose?.(); return; }
    if (window.confirm("You have unsaved changes. Discard them?")) actualClose?.();
  }, [busy, isDirty]);

  return {
    form, setForm, setField,
    busy, setBusy,
    err, setErr,
    isDirty,
    requestClose,
  };
}
